import { EyeIcon, MoreVerticalIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  parseAIClassificationMetadata,
  type DocumentCustomCategorieMapping,
  type DocumentViewerDocument,
} from '@folio/components/features/document-viewer';

import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip/tooltip';
import { DocumentClassificationButton } from '../../forms/document-classification-button';
import { DocumentDownloadButton } from './DocumentDownloadButton';

export interface DocumentRowActionsDocument {
  id: string;
  name: string;
  signedUrl?: string;
  type: DocumentViewerDocument['type'];
  category: string | null;
  metadata: Record<string, unknown>;
}

interface DocumentRowActionsProps {
  document: DocumentRowActionsDocument;
  onView?: () => void;
  categories?: DocumentCustomCategorieMapping[];
  /** Presence shows the delete-only dropdown. Callers gate this to broker context. */
  onDelete?: () => void;
}

const ACTION_BUTTON_CLASS = 'h-9 w-9 md:h-8 md:w-8 hover:bg-slate-100';

export function DocumentRowActions({
  document,
  onView,
  categories,
  onDelete,
}: DocumentRowActionsProps) {
  const { t } = useTranslation();
  const isViewable = Boolean(onView && document.signedUrl);
  const aiClassificationMetadata = parseAIClassificationMetadata(
    document.metadata?.aiclassification,
  );

  return (
    <>
      {document.metadata.scanresult ? (
        <DocumentDownloadButton document={document} showButtonText={false} />
      ) : null}
      {onView && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              onClick={() => isViewable && onView()}
              disabled={!isViewable}
              variant="ghost"
              size="icon"
              className={ACTION_BUTTON_CLASS}
              aria-label={
                isViewable ? t('fileInput.viewDocument') : t('fileInput.viewDocumentPreparing')
              }
            >
              <EyeIcon className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isViewable ? t('fileInput.viewDocument') : t('fileInput.viewDocumentPreparing')}</p>
          </TooltipContent>
        </Tooltip>
      )}
      {(document.type === 'supporting_document' ||
        document.type === 'non_delegated' ||
        document.type === 'generated') &&
        categories &&
        categories.length > 0 && (
          <DocumentClassificationButton
            documentId={document.id}
            currentCategory={document.category ? String(document.category) : undefined}
            categories={categories}
            aiClassificationMetadata={aiClassificationMetadata}
          />
        )}
      {onDelete && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={ACTION_BUTTON_CLASS}
              aria-label={t('fileInput.moreActions')}
            >
              <MoreVerticalIcon className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={onDelete}
              className="text-destructive focus:text-destructive"
            >
              <Trash2Icon className="mr-2 h-4 w-4" />
              {t('fileInput.deleteDocument')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
}
